/**
 * 대시보드(/dashboard) — 관리자 API 클라이언트
 *
 *  BE 엔드포인트(/api/admin/dashboard/*) 와 1:1 매핑.
 *  타입은 BE DTO(AdminDashboardDto.*) 와 키 이름까지 정합.
 *
 *  엔드포인트가 2개로 나뉜 이유
 *   - alerts  : "지금 처리해야 할 건수". 자주 새로고침한다.
 *   - summary : 당일 KPI + 추이. 상대적으로 무겁고 자주 바뀌지 않는다.
 */

import { apiClient } from './client';
import { API_ENDPOINTS } from '../../config/api';

/* ─────────────────────────────────────────────────────────────────
 *  타입 정의 — BE DTO 와 1:1 매핑
 * ───────────────────────────────────────────────────────────────── */

/** 처리 대기 건수 (액션 큐). */
export interface DashboardAlerts {
  /** 집계 시각 (ISO-8601 KST) */
  baseAt: string;
  /** 매장 활성화 요청 대기 */
  companyActivationRequested: number;
  /** 예약 승인 대기 (기한이 지나지 않아 승인/거절 가능한 건) */
  reservationPending: number;
  /** 기한이 지난 미처리 예약 요청 (승인해도 효력 없음 — 보조 표시용) */
  reservationPendingOverdue: number;
  /** 미지급 정산 건수 (ONLINE, PENDING) */
  settlementUnpaidCount: number;
  /** 미지급 정산 금액 합계 (원) */
  settlementUnpaidAmount: number;
  /** 크레딧 잔액 부족 사업장 수 */
  creditLowBalanceCount: number;
  /** 잔액 부족 판정 임계값 (BE 설정값) */
  creditLowBalanceThreshold: number;
  /** 최근 24시간 알림톡 발송 실패 */
  kakaoAlimFailed24h: number;
  /** 발송 대기중인 예약 Push */
  pushScheduled: number;
}

/** 예약 상태 코드 (HIST_RESERVATION.CD_RESERV_STATUS). */
export type DashboardReservStatus =
  | 'RESERV_STATUS_TEMP'
  | 'RESERV_STATUS_REQUESTED'
  | 'RESERV_STATUS_UPDATED'
  | 'RESERV_STATUS_CONFIRMED'
  | 'RESERV_STATUS_CANCELED'
  | 'RESERV_STATUS_COMPLETED'
  | 'UNKNOWN';

export interface ReservStatusCount {
  status: DashboardReservStatus;
  count: number;
}

export interface TrendPoint {
  /** YYYY-MM-DD */
  date: string;
  reservationCount: number;
  ticketPaidAmount: number;
}

/**
 * 당일 운영 지표 + 최근 추이.
 *
 *  ⚠ 금액 2종은 성격이 다르므로 화면에서도 절대 합산하지 않는다.
 *   - ticketPaidAmount    : 보호자가 이용권에 지불한 거래액(GMV). 대부분 매장으로 정산된다.
 *   - creditChargedAmount : 사업자가 멍투데이에 지불한 크레딧 충전액. 이쪽이 자사 수익.
 */
export interface DashboardSummary {
  /** YYYY-MM-DD */
  baseDate: string;
  baseAt: string;

  reservationTotal: number;
  reservationByStatus: ReservStatusCount[];
  reservationTotalPrevDay: number;

  attendExpected: number;
  attendChecked: number;
  dismissChecked: number;

  ticketPaidCount: number;
  ticketPaidAmount: number;
  ticketPaidAmountPrevDay: number;

  creditChargedCount: number;
  creditChargedAmount: number;
  creditUsed: number;

  newGuardians: number;
  newBusinessUsers: number;
  activeCompanies: number;
  totalGuardians: number;

  trend: TrendPoint[];
}

export interface SummaryParams {
  /** 기준 일자 (YYYY-MM-DD). 미지정 시 서버 기준 오늘. */
  date?: string;
  /** 추이 일수 (1~90, 기본 14) */
  trendDays?: number;
}

/* ─────────────────────────────────────────────────────────────────
 *  응답 언래핑
 * ───────────────────────────────────────────────────────────────── */

/** BaseResponse 응답에서 data 만 안전하게 꺼냄 (래퍼 유무에 모두 대응). */
const unwrap = <T,>(resp: { data?: any }): T => {
  if (resp?.data?.data !== undefined) return resp.data.data as T;
  return resp?.data as T;
};

const E = API_ENDPOINTS.ADMIN_DASHBOARD;

/* ─────────────────────────────────────────────────────────────────
 *  API 모듈
 * ───────────────────────────────────────────────────────────────── */

export const dashboardApi = {
  /** 처리 대기 건수 (액션 큐). */
  async getAlerts(): Promise<DashboardAlerts> {
    return unwrap<DashboardAlerts>(await apiClient.get(E.ALERTS));
  },

  /** 당일 운영 지표 + 최근 추이. */
  async getSummary(params: SummaryParams = {}): Promise<DashboardSummary> {
    return unwrap<DashboardSummary>(await apiClient.get(E.SUMMARY, { params }));
  },
};

/* ─────────────────────────────────────────────────────────────────
 *  표시용 상수
 * ───────────────────────────────────────────────────────────────── */

/** 예약 상태 한글 라벨. */
export const RESERV_STATUS_LABEL: Record<string, string> = {
  RESERV_STATUS_TEMP: '임시저장',
  RESERV_STATUS_REQUESTED: '승인대기',
  RESERV_STATUS_UPDATED: '변경요청',
  RESERV_STATUS_CONFIRMED: '확정',
  RESERV_STATUS_CANCELED: '취소',
  RESERV_STATUS_COMPLETED: '종료',
  UNKNOWN: '미분류',
};
